import { useRegisterSW } from "virtual:pwa-register/react";
import { RefreshCw, WifiOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Full navigation so the waiting service worker becomes controller. */
function reloadForUpdate() {
    window.location.reload();
}

/**
 * Ask the waiting worker to skipWaiting, then reload once it controls the page.
 * vite-plugin-pwa's updateServiceWorker() only posts SKIP_WAITING (since v0.13.2
 * it no longer reloads itself); reload depends on controllerchange, which needs
 * clientsClaim in the SW — with a timeout fallback for already-installed SWs.
 */
async function activateWaitingWorker(
    updateServiceWorker: (reloadPage?: boolean) => Promise<void>,
) {
    let reloaded = false;
    const reloadOnce = () => {
        if (reloaded) return;
        reloaded = true;
        reloadForUpdate();
    };

    navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, {
        once: true,
    });

    try {
        await updateServiceWorker();
    } catch {
        // Still reload so the user is not stuck on a stale build.
    }

    window.setTimeout(reloadOnce, 500);
}

/**
 * Service worker lifecycle UI:
 * - "offlineReady": first install finished, the app shell now works offline.
 * - "needRefresh": a new build was deployed; user chooses when to reload so
 *   an active forecast session is never interrupted.
 */
export function PWAUpdatePrompt() {
    const {
        offlineReady: [offlineReady, setOfflineReady],
        needRefresh: [needRefresh, setNeedRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        onNeedReload: reloadForUpdate,
        onRegisteredSW(_swUrl, registration) {
            if (!registration) return;
            // Check soon after open (stale SW was serving pre-#99 bundles that
            // still painted "Something went wrong" for sdoHmi failures), then hourly.
            void registration.update();
            const onVisible = () => {
                if (document.visibilityState === "visible") {
                    void registration.update();
                }
            };
            document.addEventListener("visibilitychange", onVisible);
            const intervalId = window.setInterval(() => {
                void registration.update();
            }, 60 * 60 * 1000);
            // Registration callback has no unmount hook; clear on pagehide.
            window.addEventListener(
                "pagehide",
                () => {
                    document.removeEventListener("visibilitychange", onVisible);
                    window.clearInterval(intervalId);
                },
                { once: true },
            );
        },
    });

    const close = () => {
        setOfflineReady(false);
        setNeedRefresh(false);
    };

    if (!offlineReady && !needRefresh) return null;

    return (
        <div
            role="status"
            className="fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-[1100] mx-auto flex max-w-md items-center gap-3 rounded-xl border border-border bg-popover/95 p-3 text-popover-foreground shadow-lg backdrop-blur supports-[backdrop-filter]:bg-popover/85"
        >
            {needRefresh ? (
                <RefreshCw className="size-4 shrink-0 text-primary" aria-hidden="true" />
            ) : (
                <WifiOff className="size-4 shrink-0 text-primary" aria-hidden="true" />
            )}
            <p className="flex-1 text-sm leading-snug">
                {needRefresh
                    ? "A new version of Aurora Forecaster is available."
                    : "Ready for offline use — the app now works without a connection."}
            </p>
            {needRefresh && (
                <Button
                    type="button"
                    size="sm"
                    onClick={() => void activateWaitingWorker(updateServiceWorker)}
                >
                    Reload
                </Button>
            )}
            <Button
                size="icon"
                variant="ghost"
                onClick={close}
                aria-label="Dismiss"
                className="size-7"
            >
                <X className="size-4" aria-hidden="true" />
            </Button>
        </div>
    );
}
